import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { ServerMessage } from '../../../models/server-message';
import { Clock } from '../../../utils/clock';
import { Notice } from '../../../models/notice';
import { NoticeTypeEnum } from '../../../enum/notice-type.enum';
import { NoticeLevelEnum } from '../../../enum/notice-level.enum';
import { Location } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class LocaleService {

    constructor(@Inject(LOCALE_ID) private locale: string, private location: Location) {}

    private readonly SUPPORTED_LANGUAGES = [ 'en', 'fr' ];
    private readonly DEFAULT_LANGUAGE = 'en';

    getUserLocale(): string {
        return this.locale;
        if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
            return 'en-US';
        }
        const wn = window.navigator as any;
        let lang = wn.languages ? wn.languages[0] : 'en-US';
        lang = lang || wn.language || wn.browserLanguage || wn.userLanguage;
        return lang;
    }

    getUserLocaleArray(): string[] {
        const locale = this.getUserLocale();
        const list = [ locale ];
        if (locale.indexOf('-') > -1) {
            const majorLocale = locale.substring(0, locale.indexOf('-'));
            list.push(majorLocale);
        }
        if (list.indexOf('en') === -1) {
            list.push('en');
        }
        return list;
    }

    getSupportedUserLocale(): string {
        const locale = this.getUserLocale().substring(0, 1);
        if (this.SUPPORTED_LANGUAGES.indexOf(locale) === -1) {
            return this.DEFAULT_LANGUAGE;
        } else {
            return locale;
        }
    }

    createServerMessage(text: string): ServerMessage {
        return { type: 'NOTICE', level: 'CONSUMER', parameters: {}, translatable: text, localized: { '*': text } };
    }

    renderServerMessage(message: ServerMessage): string {
        const localeArray = this.getUserLocaleArray();
        for (const locale of localeArray) {
            if (message.localized.hasOwnProperty(locale)) {
                return message.localized[locale];
            }
        }
        if (message.localized.hasOwnProperty('*')) {
            return message.localized['*'];
        }
        return message.translatable;
    }

    serverMessageToNotice(message: ServerMessage): Notice
    {
        const [ title, preview ] = this.renderServerMessage(message).split(/:(.*)/s);
        return {
            id: title + '-' + preview,
            title,
            preview,
            type: NoticeTypeEnum.QUOTE,
            level: NoticeLevelEnum.WARNING,
            text: preview,
            date: Clock.now()
        };
    }

    activeLocale(): string {
        const urlParts = window.location.pathname.split('/');
        if (urlParts.length > 1 && this.SUPPORTED_LANGUAGES.indexOf(urlParts[1]) > -1) {
            return urlParts[1];
        }
        return 'en';
    }

    supportedLocales(): string[] {
        return this.SUPPORTED_LANGUAGES;
    }

    getLanguagePath(locale: string): string {
        // The full browser URL including query params and hash
        const full = this.location.path(true);       // e.g. "/en/shipment/123/view?x=1#tabA"
        const url = new URL(full, window.location.origin);

        // Extract path segments
        const segments = url.pathname.split('/').filter(Boolean);

        if (segments.length === 0) {
            // Edge case: no route segments
            return `/${locale}?_cb=${Date.now()}`;
        }

        // Replace first segment (current language code)
        segments[0] = locale;

        // Reconstruct path
        let newPath = '/' + segments.join('/');

        // Preserve existing query params
        const params = url.searchParams;

        // Add cache-buster (unique per request)
        params.set('_cb', Date.now().toString());

        // Reattach query + hash
        const query = params.toString();
        const hash = url.hash ?? '';

        if (query) {
            newPath += '?' + query;
        }
        if (hash) {
            newPath += hash;
        }

        return newPath;
    }


    changeLocale(locale: string): void {
        const d = Clock.inDays(365);
        document.cookie = `LOCALE=${locale}; expires=${d.toUTCString()}; path=/;`;
        window.location.assign(this.getLanguagePath(locale));
    }

}
